import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { storage } from '@uhg-abyss/web/tools/storage';
import { ProviderSelect } from '@sde/widgets-core/src/widgets/ProviderSelect';
import { useUser } from '@sde/widgets-core/src/redux/resources/user';
import { event } from '@uhg-abyss/web/tools/event';
import { useModal } from 'src/state/modal';
import { useDashboard } from 'src/state/dashboard';

export const ProviderSelectWidget = ({
  provider,
  setProvider,
  isProviderOpen,
  isHidden,
  setWidgetActions,
  openDropdown,
  closeDropdown,
  setProviderNotSaved,
  setDigitalSecurityError,
}) => {
  const { user } = useUser.state();
  const { updateUser } = useUser.actions();
  const { updateProviderPreferenceError } = useDashboard.actions();
  const { providerPreferenceError } = useDashboard.state();
  const [modifiedDropdown, setModifiedDropdown] = useState(false);
  const { modals } = useModal.state();
  const permissionsStorageKey = 'provider-permissions';

  useEffect(() => {
    const cachedPermissions = storage.local.get(permissionsStorageKey);
    if (cachedPermissions?.length > 0) {
      updateUser({ permissions: cachedPermissions });
    }
  }, []);

  useEffect(() => {
    if (isHidden) {
      setModifiedDropdown(false);
    }
  }, [isHidden]);

  useEffect(() => {
    if (isProviderOpen) {
      openDropdown();
    }
  }, [isProviderOpen]);

  const changeCareProviderStartEvent = () => {
    if (!modifiedDropdown && !isHidden) {
      event('CHANGE_CARE_PROVIDER_START');
      setModifiedDropdown(true);
    }
  };

  const widgetProps = {
    data: {
      title: '',
      isHidden,
      user,
      provider,
      providerPreferenceError,
      confirmSelection: !!modals['documents-search'],
      saveDisabled: user.isTechnicalUser, // if user is emulating, save is disabled
    },
    events: {
      getActions: (actions) => {
        setWidgetActions(actions);
      },
      onChange: (data) => {
        event.setData({
          corporateProviderName:
            data.corporateProviderName || data.provider?.corporateProviderName,
          taxIdNumber: data.taxIdNumber || data.provider?.taxIdNumber,
          corporateProviderMpin:
            data.corporateProviderMpin || data.provider?.corporateProviderMpin,
          careProviderMpin:
            data.servicingProviderMpin || data.provider?.servicingProviderMpin,
          careProviderName:
            data.servicingProviderName || data.provider?.servicingProviderName,
          careProviderFirstName:
            data.servicingProviderFirstName ||
            data.provider?.servicingProviderFirstName,
          careProviderLastName:
            data.servicingProviderLastName ||
            data.provider?.servicingProviderLastName,
          allPermission: data.allPermission || false,
          permissionCode: data.permissionCode || [],
        });
        setProviderNotSaved(false);
        storage.local.set(permissionsStorageKey, data.permissions);

        if (
          data?.permissions?.length > 0 ||
          data?.permissionSubCode?.length > 0 ||
          data?.permissionCode?.length > 0 ||
          data?.allPermission
        ) {
          setProvider({
            ...data.provider,
            permissions: data.permissions,
            privileges: data.organizations,
            allPermission: data.allPermission,
            permissionSubCode: data.permissionSubCode || [],
            permissionCode: data.permissionCode || [],
          });
          updateUser({
            permissions: data.permissions,
            privileges: data.organizations,
            allPermission: data.allPermission,
            permissionSubCode: data.permissionSubCode || [],
            permissionCode: data.permissionCode || [],
            provider: data.provider,
          });
        } else {
          setProvider(data.provider);
        }
      },
      onSave: () => {
        closeDropdown();
        setDigitalSecurityError({
          isDigitalSecurityError: false,
          isProviderPreferenceError: false,
        });
        event('UPDATE_PROVIDER_SELECTION');
        event('CHANGE_CARE_PROVIDER_COMPLETE');
        event('CHANGE_CARE_PROVIDER_SAVE');
        // clear any previous errors
        updateProviderPreferenceError({});
      },
      onCancel: () => {
        closeDropdown();
      },
      onError: () => {
        storage.local.remove(permissionsStorageKey);
        setProviderNotSaved(true);
      },
      onOrganizationChange: changeCareProviderStartEvent,
      onCorporateChange: changeCareProviderStartEvent,
      onTaxIdNumberChange: changeCareProviderStartEvent,
      onCareProviderChange: changeCareProviderStartEvent,
      onSaveCorporates: (data) => {
        if (data.length > 0) {
          updateUser({
            corporates: data,
          });
        }
      },
      onDigitalSecurityError: (err) => {
        setDigitalSecurityError(err);
        updateUser({
          code: err.code,
        });
      },
      updateAdminInfo: (data) => {
        updateUser({ adminInfo: data });
      },
    },
  };

  return <ProviderSelect {...widgetProps} />;
};

ProviderSelectWidget.propTypes = {
  provider: PropTypes.shape({}).isRequired,
  setProvider: PropTypes.func.isRequired,
  isProviderOpen: PropTypes.bool.isRequired,
  isHidden: PropTypes.bool.isRequired,
  openDropdown: PropTypes.func.isRequired,
  closeDropdown: PropTypes.func.isRequired,
  setWidgetActions: PropTypes.func.isRequired,
  setProviderNotSaved: PropTypes.func.isRequired,
  setDigitalSecurityError: PropTypes.func.isRequired,
};
